// In-memory mock data for the CertiCore demo. No backend.

export interface Standard {
  codigo: string;
  titulo: string;
  sector: string;
  comite?: string;
  vigencia: string;
  estado: "borrador" | "en-revision" | "publicado";
  elementos: number;
  criterios: number;
  candidatosActivos: number;
  certificadosEmitidos: number;
  ultimaActualizacion: string;
  // Campos del Punto 1 del documento (opcionales para compatibilidad con seed)
  tipo?: string;
  version?: string;
  periodoRevision?: string;
  descripcion?: string;
  proposito?: string;
  documentoFuente?: { nombre: string; tamanioKb: number; fechaCarga: string };
}

export const standards: Standard[] = [
  {
    codigo: "EC1781",
    titulo: "Conducción de motocicleta de alto desempeño en operativos de seguridad",
    sector: "Seguridad pública",
    comite: "Comité de gestión por competencias — SEDENA / GN",
    vigencia: "5 años",
    estado: "publicado",
    elementos: 2,
    criterios: 28,
    candidatosActivos: 24,
    certificadosEmitidos: 187,
    ultimaActualizacion: "2026-04-12",
  },
  {
    codigo: "EC0301",
    titulo: "Diseño de cursos de formación del capital humano de manera presencial grupal",
    sector: "Educación y formación",
    comite: "Comité de capital humano",
    vigencia: "5 años",
    estado: "publicado",
    elementos: 3,
    criterios: 22,
    candidatosActivos: 12,
    certificadosEmitidos: 612,
    ultimaActualizacion: "2025-11-03",
  },
  {
    codigo: "EC0217",
    titulo: "Impartición de cursos de formación del capital humano de manera presencial",
    sector: "Educación y formación",
    comite: "Comité de capital humano",
    vigencia: "5 años",
    estado: "publicado",
    elementos: 3,
    criterios: 19,
    candidatosActivos: 8,
    certificadosEmitidos: 1453,
    ultimaActualizacion: "2025-09-21",
  },
  {
    codigo: "EC0435",
    titulo: "Prestación de servicios de atención a clientes en establecimientos comerciales",
    sector: "Comercio",
    comite: "Comité de servicios comerciales",
    vigencia: "5 años",
    estado: "borrador",
    elementos: 0,
    criterios: 0,
    candidatosActivos: 0,
    certificadosEmitidos: 0,
    ultimaActualizacion: "2026-05-22",
  },
];

export interface Element {
  id: string;
  codigo: string;
  estandar: string;
  titulo: string;
  descripcion: string;
  criterios: Criterio[];
  evidencias: Evidencia[];
  instrumentos: Instrumento[];
  // Punto 3 del flujo: ficha enriquecida del elemento
  orden?: number;
  tipoEvaluacionPrincipal?: string;
  glosario?: GlosarioTermino[];
  estadoElemento?: "borrador" | "activo";
}

export interface GlosarioTermino {
  termino: string;
  definicion: string;
}

export type TipoEvidenciaCriterio = "Foto" | "Video" | "Observación" | "Formulario" | "Audio";

export interface Criterio {
  id: string;
  codigo: string;
  descripcion: string;
  tipo: "desempeño" | "producto" | "conocimiento" | "actitud" | "emergencia";
  ponderacion: number;
  // Sub-incisos (viñetas del criterio dentro del documento original)
  subitems?: string[];
  // Ficha extendida del criterio
  nombreCorto?: string;
  obligatorio?: boolean;
  critico?: boolean;
  formaEvaluacion?: "Lista de cotejo" | "Rúbrica" | "Examen teórico" | "Guía de observación";
  requiereEvidencia?: boolean;
  tiposEvidencia?: TipoEvidenciaCriterio[];
  observacionObligatoria?: boolean;
  resultadosPermitidos?: string[];
  instrumentoAsociado?: string;
  moduloLMS?: string;
  reglaIA?: string;
}

export interface Evidencia {
  id: string;
  nombre: string;
  tipo: "video" | "foto" | "documento" | "audio";
  obligatoria: boolean;
  criterioIds?: string[];
}

export interface InstrumentItem {
  id: string;
  texto: string;
  criterioId?: string;
}

export interface Instrumento {
  id: string;
  nombre: string;
  tipo: "checklist" | "rúbrica" | "examen" | "observación";
  reactivos: number;
  elementoId?: string;
  criterioIds?: string[];
  items?: InstrumentItem[];
}

export const elements: Element[] = [
  {
    id: "elem-e5637",
    codigo: "E5637",
    estandar: "EC1781",
    titulo: "Revisar la motocicleta y el equipo de protección personal previo a la operación",
    descripcion:
      "Verificar el estado mecánico, hidráulico y eléctrico de la motocicleta de alto desempeño, así como el equipo de protección personal del operador, conforme a la lista de cotejo institucional.",
    criterios: [
      { id: "c-e5637-d1", codigo: "D1", descripcion: "Recibe la motocicleta al inicio/cambio de turno", tipo: "desempeño", ponderacion: 6 },
      { id: "c-e5637-d2", codigo: "D2", descripcion: "Revisa la parte frontal de la motocicleta", tipo: "desempeño", ponderacion: 10 },
      { id: "c-e5637-d3", codigo: "D3", descripcion: "Revisa el lado derecho de la motocicleta", tipo: "desempeño", ponderacion: 12 },
      { id: "c-e5637-d4", codigo: "D4", descripcion: "Revisa la parte trasera de la motocicleta", tipo: "desempeño", ponderacion: 10 },
      { id: "c-e5637-d5", codigo: "D5", descripcion: "Revisa el lado izquierdo de la motocicleta", tipo: "desempeño", ponderacion: 12 },
      { id: "c-e5637-d6", codigo: "D6", descripcion: "Revisa la motocicleta con motor encendido", tipo: "desempeño", ponderacion: 12 },
      { id: "c-e5637-d7", codigo: "D7", descripcion: "Revisa el equipo de protección/seguridad", tipo: "desempeño", ponderacion: 6 },
      { id: "c-e5637-p1", codigo: "P1", descripcion: "Formato de entrega-recepción de revisión de motocicleta, requisitado", tipo: "producto", ponderacion: 16 },
      { id: "c-e5637-c1", codigo: "C1", descripcion: "Equipo de protección/seguridad para la conducción de motocicleta (nivel: Conocimiento)", tipo: "conocimiento", ponderacion: 8 },
      { id: "c-e5637-a1", codigo: "A1", descripcion: "Orden — secuencia de revisión en sentido de las manecillas del reloj (frontal → derecho → trasero → izquierdo → encendido)", tipo: "actitud", ponderacion: 8 },
    ],
    evidencias: [
      { id: "ev-1", nombre: "Formato de entrega-recepción de revisión de motocicleta", tipo: "documento", obligatoria: true, criterioIds: ["c-e5637-p1"] },
      { id: "ev-2", nombre: "Fotografías de la motocicleta por zona (4 ángulos)", tipo: "foto", obligatoria: true, criterioIds: ["c-e5637-d2", "c-e5637-d3", "c-e5637-d4", "c-e5637-d5"] },
      { id: "ev-3", nombre: "Video de inspección con verbalización", tipo: "video", obligatoria: true, criterioIds: ["c-e5637-d1", "c-e5637-d6", "c-e5637-a1"] },
      { id: "ev-4", nombre: "Fotografía del equipo de protección personal", tipo: "foto", obligatoria: true, criterioIds: ["c-e5637-d7"] },
    ],
    instrumentos: [
      { id: "in-1", nombre: "Lista de cotejo de revisión preoperativa", tipo: "checklist", reactivos: 65, elementoId: "elem-e5637", criterioIds: ["c-e5637-d1", "c-e5637-d2", "c-e5637-d3", "c-e5637-d4", "c-e5637-d5", "c-e5637-d6", "c-e5637-d7", "c-e5637-p1"] },
      { id: "in-2", nombre: "Guía de observación de orden y secuencia", tipo: "observación", reactivos: 7, elementoId: "elem-e5637", criterioIds: ["c-e5637-a1"] },
      { id: "in-2b", nombre: "Examen teórico — EPP y seguridad", tipo: "examen", reactivos: 30, elementoId: "elem-e5637", criterioIds: ["c-e5637-c1"] },
    ],
  },
  {
    id: "elem-e5638",
    codigo: "E5638",
    estandar: "EC1781",
    titulo: "Conducir la motocicleta en circuito de alto desempeño bajo condiciones operativas",
    descripcion:
      "Ejecutar maniobras tácticas en circuito cerrado: escolta, persecución controlada, frenado de emergencia, esquive y conducción a baja velocidad, manteniendo seguridad operacional.",
    criterios: [
      { id: "c-e5638-d1", codigo: "D1", descripcion: "Ejercicio de equilibrio y balance", tipo: "desempeño", ponderacion: 7 },
      { id: "c-e5638-d2", codigo: "D2", descripcion: "Ejercicio de dirección a tope", tipo: "desempeño", ponderacion: 7 },
      { id: "c-e5638-d3", codigo: "D3", descripcion: "Ejercicio de maniobrabilidad y visión progresiva", tipo: "desempeño", ponderacion: 8 },
      { id: "c-e5638-d4", codigo: "D4", descripcion: "Ejercicio de capacidad de frenado", tipo: "desempeño", ponderacion: 9 },
      { id: "c-e5638-d5", codigo: "D5", descripcion: "Ejercicio de capacidad de frenado en curva", tipo: "desempeño", ponderacion: 9 },
      { id: "c-e5638-d6", codigo: "D6", descripcion: "Ejercicio de efecto giroscópico / contramanillar", tipo: "desempeño", ponderacion: 7 },
      { id: "c-e5638-d7", codigo: "D7", descripcion: "Ejercicio de cambio de carril por emergencia", tipo: "desempeño", ponderacion: 9 },
      { id: "c-e5638-d8", codigo: "D8", descripcion: "Ejercicio con reacción sorpresa", tipo: "desempeño", ponderacion: 9 },
      { id: "c-e5638-d9", codigo: "D9", descripcion: "Ejercicio de slalom corto", tipo: "desempeño", ponderacion: 6 },
      { id: "c-e5638-d10", codigo: "D10", descripcion: "Ejercicio de slalom largo", tipo: "desempeño", ponderacion: 6 },
      { id: "c-e5638-d11", codigo: "D11", descripcion: "Ejercicio de óvalo de posiciones", tipo: "desempeño", ponderacion: 7 },
      { id: "c-e5638-d12", codigo: "D12", descripcion: "Ejercicio de trayectorias ideales", tipo: "desempeño", ponderacion: 6 },
      { id: "c-e5638-c1", codigo: "C1", descripcion: "Conocimiento de técnicas de alto rendimiento (examen teórico)", tipo: "conocimiento", ponderacion: 4 },
      { id: "c-e5638-a1", codigo: "A1", descripcion: "Orden — respeto a la secuencia indicada por el responsable", tipo: "actitud", ponderacion: 2 },
      { id: "c-e5638-a2", codigo: "A2", descripcion: "Responsabilidad — uso de EPP en pista durante toda la sesión", tipo: "actitud", ponderacion: 2 },
      { id: "c-e5638-em1", codigo: "EM", descripcion: "Respuesta ante caída/accidente — activa protocolo y atiende emergencia", tipo: "desempeño", ponderacion: 1 },
    ],
    evidencias: [
      { id: "ev-5", nombre: "Video del recorrido completo en circuito (cámara fija y casco)", tipo: "video", obligatoria: true, criterioIds: ["c-e5638-d1", "c-e5638-d2", "c-e5638-d3", "c-e5638-d4", "c-e5638-d5", "c-e5638-d6", "c-e5638-d7", "c-e5638-d8", "c-e5638-d9", "c-e5638-d10", "c-e5638-d11", "c-e5638-d12"] },
      { id: "ev-6", nombre: "Reporte de telemetría GPS y tiempos por ejercicio", tipo: "documento", obligatoria: true, criterioIds: ["c-e5638-d4", "c-e5638-d5", "c-e5638-d9", "c-e5638-d10", "c-e5638-d12"] },
      { id: "ev-7", nombre: "Fotografía del montaje del circuito con referencias", tipo: "foto", obligatoria: true, criterioIds: ["c-e5638-d3", "c-e5638-d7"] },
      { id: "ev-8", nombre: "Audio de comunicación radial durante la evaluación", tipo: "audio", obligatoria: false, criterioIds: ["c-e5638-a1", "c-e5638-a2", "c-e5638-em1"] },
      { id: "ev-9", nombre: "Bitácora de observaciones del evaluador por ejercicio", tipo: "documento", obligatoria: true, criterioIds: ["c-e5638-d1", "c-e5638-d2", "c-e5638-d3", "c-e5638-d4", "c-e5638-d5", "c-e5638-d6", "c-e5638-d7", "c-e5638-d8", "c-e5638-d9", "c-e5638-d10", "c-e5638-d11", "c-e5638-d12"] },
    ],
    instrumentos: [
      { id: "in-3", nombre: "Rúbrica de ejecución por ejercicio", tipo: "rúbrica", reactivos: 17, elementoId: "elem-e5638", criterioIds: ["c-e5638-d1", "c-e5638-d2", "c-e5638-d3", "c-e5638-d4", "c-e5638-d5", "c-e5638-d6", "c-e5638-d7", "c-e5638-d8", "c-e5638-d9", "c-e5638-d10", "c-e5638-d11", "c-e5638-d12"] },
      { id: "in-4", nombre: "Examen teórico — técnicas de alto rendimiento", tipo: "examen", reactivos: 30, elementoId: "elem-e5638", criterioIds: ["c-e5638-c1"] },
      { id: "in-5", nombre: "Guía de observación de respuesta ante emergencia", tipo: "observación", reactivos: 6, elementoId: "elem-e5638", criterioIds: ["c-e5638-a1", "c-e5638-a2", "c-e5638-em1"] },
    ],
  },
];

export interface Candidate {
  id: string;
  nombre: string;
  rango: string;
  unidad: string;
  curp: string;
  matricula: string;
  edad: number;
  estatura: string;
  experiencia: string;
  email: string;
  telefono: string;
  estado: "inscrito" | "en-evaluacion" | "en-dictamen" | "certificado" | "no-competente";
  progreso: number;
  ultimaActividad: string;
  avatarColor: string;
  iniciales: string;
}

export const candidates: Candidate[] = [
  { id: "cand-001", nombre: "Sgto. 1° Jorge Ramírez Pacheco", rango: "Sargento 1°", unidad: "Guardia Nacional — División Caminos", curp: "RAPJ850612HDFMRR04", matricula: "GN-204815", edad: 38, estatura: "1.78 m", experiencia: "12 años en motocicleta", email: "j.ramirez@gn.gob.mx", telefono: "55 1234 5678", estado: "en-dictamen", progreso: 92, ultimaActividad: "2026-05-26 14:22", avatarColor: "#0e7490", iniciales: "JR" },
  { id: "cand-002", nombre: "Cb. Mariana Hernández Soto", rango: "Cabo", unidad: "Guardia Nacional — DF Centro", curp: "HESM901105MDFRNR09", matricula: "GN-209412", edad: 34, estatura: "1.65 m", experiencia: "7 años en motocicleta", email: "m.hernandez@gn.gob.mx", telefono: "55 2345 6789", estado: "en-evaluacion", progreso: 78, ultimaActividad: "2026-05-26 11:08", avatarColor: "#1a2744", iniciales: "MH" },
  { id: "cand-003", nombre: "Sgto. 2° Luis Aguilar Mendoza", rango: "Sargento 2°", unidad: "Guardia Nacional — Edomex", curp: "AUML880318HMCGNS06", matricula: "GN-198877", edad: 36, estatura: "1.81 m", experiencia: "10 años en motocicleta", email: "l.aguilar@gn.gob.mx", telefono: "55 3456 7890", estado: "en-evaluacion", progreso: 65, ultimaActividad: "2026-05-25 17:44", avatarColor: "#16a34a", iniciales: "LA" },
  { id: "cand-004", nombre: "Cb. Roberto Gutiérrez Núñez", rango: "Cabo", unidad: "Guardia Nacional — Jalisco", curp: "GUNR871220HJCTXB02", matricula: "GN-211503", edad: 37, estatura: "1.74 m", experiencia: "9 años en motocicleta", email: "r.gutierrez@gn.gob.mx", telefono: "33 4567 8901", estado: "inscrito", progreso: 38, ultimaActividad: "2026-05-24 09:12", avatarColor: "#d97706", iniciales: "RG" },
  { id: "cand-005", nombre: "Sgto. 1° Patricia Vázquez León", rango: "Sargento 1°", unidad: "Guardia Nacional — Nuevo León", curp: "VALP830915MNLZNT01", matricula: "GN-187621", edad: 41, estatura: "1.69 m", experiencia: "15 años en motocicleta", email: "p.vazquez@gn.gob.mx", telefono: "81 5678 9012", estado: "certificado", progreso: 100, ultimaActividad: "2026-05-20 16:31", avatarColor: "#0891b2", iniciales: "PV" },
  { id: "cand-006", nombre: "Cb. Andrés Martínez Cruz", rango: "Cabo", unidad: "Guardia Nacional — Querétaro", curp: "MACA920708HQTLRN05", matricula: "GN-215890", edad: 33, estatura: "1.76 m", experiencia: "5 años en motocicleta", email: "a.martinez@gn.gob.mx", telefono: "44 6789 0123", estado: "inscrito", progreso: 22, ultimaActividad: "2026-05-23 13:55", avatarColor: "#dc2626", iniciales: "AM" },
  { id: "cand-007", nombre: "Sgto. 2° Diego Ortega Reyes", rango: "Sargento 2°", unidad: "Policía Federal de Caminos", curp: "OERD860424HDFRYG08", matricula: "PF-098344", edad: 39, estatura: "1.82 m", experiencia: "11 años en motocicleta", email: "d.ortega@pfc.gob.mx", telefono: "55 7890 1234", estado: "inscrito", progreso: 12, ultimaActividad: "2026-05-22 10:14", avatarColor: "#475569", iniciales: "DO" },
  { id: "cand-008", nombre: "Cb. Sofía Reyes Domínguez", rango: "Cabo", unidad: "Guardia Nacional — Puebla", curp: "REDS911019MPLYMF03", matricula: "GN-220114", edad: 34, estatura: "1.62 m", experiencia: "6 años en motocicleta", email: "s.reyes@gn.gob.mx", telefono: "22 8901 2345", estado: "inscrito", progreso: 8, ultimaActividad: "2026-05-21 15:48", avatarColor: "#7c3aed", iniciales: "SR" },
  { id: "cand-009", nombre: "Sgto. 1° Fernando López Rivera", rango: "Sargento 1°", unidad: "SSC CDMX — División Tránsito", curp: "LORF820527HDFPVR01", matricula: "SSC-024851", edad: 43, estatura: "1.79 m", experiencia: "18 años en motocicleta", email: "f.lopez@ssc.cdmx.gob.mx", telefono: "55 9012 3456", estado: "certificado", progreso: 100, ultimaActividad: "2026-05-18 11:00", avatarColor: "#16a34a", iniciales: "FL" },
  { id: "cand-010", nombre: "Cb. Karla Jiménez Bautista", rango: "Cabo", unidad: "Guardia Nacional — Veracruz", curp: "JIBK930814MVZMTR07", matricula: "GN-225672", edad: 32, estatura: "1.67 m", experiencia: "5 años en motocicleta", email: "k.jimenez@gn.gob.mx", telefono: "22 0123 4567", estado: "no-competente", progreso: 100, ultimaActividad: "2026-05-15 09:30", avatarColor: "#dc2626", iniciales: "KJ" },
  { id: "cand-011", nombre: "Sgto. 2° Héctor Morales Cano", rango: "Sargento 2°", unidad: "Guardia Nacional — Sonora", curp: "MOCH870302HSRLNC02", matricula: "GN-201117", edad: 38, estatura: "1.80 m", experiencia: "10 años en motocicleta", email: "h.morales@gn.gob.mx", telefono: "66 1234 5678", estado: "inscrito", progreso: 0, ultimaActividad: "—", avatarColor: "#0e7490", iniciales: "HM" },
  { id: "cand-012", nombre: "Cb. Beatriz Castillo Reyes", rango: "Cabo", unidad: "Guardia Nacional — Yucatán", curp: "CARB910128MYNSYT04", matricula: "GN-228301", edad: 34, estatura: "1.64 m", experiencia: "6 años en motocicleta", email: "b.castillo@gn.gob.mx", telefono: "99 2345 6789", estado: "inscrito", progreso: 0, ultimaActividad: "—", avatarColor: "#0891b2", iniciales: "BC" },
];

export interface StaffMember {
  id: string;
  nombre: string;
  rol: string;
  cedula: string;
  iniciales: string;
}

export const evaluadores: StaffMember[] = [
  { id: "eval-001", nombre: "Mtro. Ricardo Salinas Vega", rol: "Evaluador Senior certificado EC0076", cedula: "CONOCER-EV-2387", iniciales: "RS" },
  { id: "eval-002", nombre: "Lic. Adriana Cruz Robledo", rol: "Evaluadora EC0076", cedula: "CONOCER-EV-2412", iniciales: "AC" },
  { id: "eval-003", nombre: "Cap. Manuel Treviño Pardo", rol: "Evaluador especialista motocicleta", cedula: "CONOCER-EV-2455", iniciales: "MT" },
];

export const dictaminadores: StaffMember[] = [
  { id: "dict-001", nombre: "Dr. Ernesto Vargas Ibañez", rol: "Dictaminador académico — Comité EC1781", cedula: "CONOCER-DI-0915", iniciales: "EV" },
  { id: "dict-002", nombre: "Mtra. Lorena Mendoza Salgado", rol: "Dictaminadora — Comité Seguridad", cedula: "CONOCER-DI-0987", iniciales: "LM" },
];

export const auditores: StaffMember[] = [
  { id: "aud-001", nombre: "C.P. Verónica Aguirre Tello", rol: "Auditora interna — Calidad", cedula: "CC-AUD-3441", iniciales: "VA" },
];

export const instructores: StaffMember[] = [
  { id: "inst-001", nombre: "Cap. Manuel Treviño Pardo", rol: "Instructor de circuito EC1781", cedula: "INS-EC1781-04", iniciales: "MT" },
  { id: "inst-002", nombre: "Sgto. 1° Ramón Fuentes Olmos", rol: "Instructor adjunto", cedula: "INS-EC1781-09", iniciales: "RF" },
];

export interface Group {
  id: string;
  nombre: string;
  estandar: string;
  generacion: string;
  inicio: string;
  fin: string;
  sede: string;
  instructorId: string;
  evaluadorId: string;
  evaluadoresIds?: string[];
  cupo: number;
  candidatosIds: string[];
  estado: "planeado" | "en-curso" | "finalizado";
  documentos?: GroupDocument[];
}

export interface GroupDocument {
  id: string;
  nombre: string;
  tipo: "convenio" | "lista" | "plan" | "acta" | "otro";
  fecha: string;
  subidoPor: string;
  notas?: string;
}

export type GroupSessionTipo = "induccion" | "teoria" | "practica" | "examen" | "cierre";
export interface GroupSession {
  id: string;
  grupoId: string;
  titulo: string;
  tipo: GroupSessionTipo;
  fecha: string;
  hora: string;
  duracionMin: number;
  sede: string;
  instructorId?: string;
  evaluadorId?: string;
  notas?: string;
  descripcion?: string;
  ejerciciosCubiertos?: string[];
}

export const groups: Group[] = [
  {
    id: "grp-2026-1",
    nombre: "EC1781 — Generación 2026-A",
    estandar: "EC1781",
    generacion: "2026-A",
    inicio: "2026-05-12",
    fin: "2026-06-30",
    sede: "Centro de Adiestramiento Iztapalapa, CDMX",
    instructorId: "inst-001",
    evaluadorId: "eval-003",
    evaluadoresIds: ["eval-003"],
    cupo: 12,
    candidatosIds: ["cand-001", "cand-002", "cand-003", "cand-004", "cand-005", "cand-006", "cand-007", "cand-008"],
    estado: "en-curso",
    documentos: [
      { id: "doc-g1-1", nombre: "Convenio interinstitucional GN-CONOCER", tipo: "convenio", fecha: "2026-04-30", subidoPor: "Mtra. Alicia Aguirre" },
      { id: "doc-g1-2", nombre: "Lista de asistencia inducción", tipo: "lista", fecha: "2026-05-12", subidoPor: "Cap. Manuel Treviño" },
    ],
  },
  {
    id: "grp-2026-2",
    nombre: "EC1781 — Generación 2026-B",
    estandar: "EC1781",
    generacion: "2026-B",
    inicio: "2026-06-15",
    fin: "2026-08-02",
    sede: "Centro de Adiestramiento Iztapalapa, CDMX",
    instructorId: "inst-002",
    evaluadorId: "eval-002",
    evaluadoresIds: ["eval-002"],
    cupo: 10,
    candidatosIds: ["cand-009", "cand-010", "cand-011", "cand-012"],
    estado: "planeado",
    documentos: [
      { id: "doc-g2-1", nombre: "Plan calendarizado 2026-B", tipo: "plan", fecha: "2026-05-22", subidoPor: "Mtra. Alicia Aguirre" },
    ],
  },
];

export const sessionsSeed: GroupSession[] = [
  { id: "ses-g1-1", grupoId: "grp-2026-1", titulo: "Inducción", tipo: "induccion", fecha: "2026-05-12", hora: "09:00", duracionMin: 180, sede: "Aula A", instructorId: "inst-001" },
  { id: "ses-g1-2", grupoId: "grp-2026-1", titulo: "Diagnóstico", tipo: "examen", fecha: "2026-05-14", hora: "09:00", duracionMin: 120, sede: "Aula A", instructorId: "inst-001" },
  { id: "ses-g1-3", grupoId: "grp-2026-1", titulo: "Teoría 1", tipo: "teoria", fecha: "2026-05-16", hora: "09:00", duracionMin: 180, sede: "Aula B", instructorId: "inst-001" },
  { id: "ses-g1-4", grupoId: "grp-2026-1", titulo: "Teoría 2", tipo: "teoria", fecha: "2026-05-18", hora: "09:00", duracionMin: 180, sede: "Aula B", instructorId: "inst-001" },
  { id: "ses-g1-5", grupoId: "grp-2026-1", titulo: "Examen teórico", tipo: "examen", fecha: "2026-05-20", hora: "09:00", duracionMin: 120, sede: "Aula A", instructorId: "inst-001", evaluadorId: "eval-003" },
  {
    id: "ses-g1-5b", grupoId: "grp-2026-1", titulo: "Práctica E5637 — Revisión preoperativa", tipo: "practica",
    fecha: "2026-05-21", hora: "08:00", duracionMin: 180, sede: "Patio de revisión", instructorId: "inst-001",
    descripcion: "Aplicación completa de la lista de cotejo institucional. Los candidatos ejecutan la inspección de la motocicleta en sentido de las manecillas del reloj (frontal → derecho → trasero → izquierdo → encendido) y verifican el equipo de protección personal.",
    ejerciciosCubiertos: ["c-e5637-d1","c-e5637-d2","c-e5637-d3","c-e5637-d4","c-e5637-d5","c-e5637-d6","c-e5637-d7","c-e5637-p1","c-e5637-a1"],
  },
  {
    id: "ses-g1-6", grupoId: "grp-2026-1", titulo: "Práctica E5638 — Equilibrio y dirección", tipo: "practica",
    fecha: "2026-05-23", hora: "14:00", duracionMin: 240, sede: "Pista 1", instructorId: "inst-001", evaluadorId: "eval-003",
    descripcion: "Ejercicios de baja velocidad: equilibrio estático, balance en marcha lenta, dirección a tope y maniobrabilidad con visión progresiva. Base técnica para los ejercicios de mayor velocidad.",
    ejerciciosCubiertos: ["c-e5638-d1","c-e5638-d2","c-e5638-d3","c-e5638-a1","c-e5638-a2"],
  },
  {
    id: "ses-g1-7", grupoId: "grp-2026-1", titulo: "Práctica E5638 — Frenado y curvas", tipo: "practica",
    fecha: "2026-05-25", hora: "14:00", duracionMin: 240, sede: "Pista 1", instructorId: "inst-001", evaluadorId: "eval-001",
    descripcion: "Capacidad de frenado en línea recta y en curva con telemetría en tiempo real. Se registra la distancia de parada a 60 km/h (umbral ≤ 32 m) y el ángulo de inclinación en curva. Incluye ejercicio de efecto giroscópico y contramanillar.",
    ejerciciosCubiertos: ["c-e5638-d4","c-e5638-d5","c-e5638-d6","c-e5638-a1","c-e5638-a2"],
  },
  {
    id: "ses-g1-p4", grupoId: "grp-2026-1", titulo: "Práctica E5638 — Maniobras evasivas", tipo: "practica",
    fecha: "2026-05-28", hora: "14:00", duracionMin: 240, sede: "Pista 1", instructorId: "inst-001", evaluadorId: "eval-003",
    descripcion: "Cambio de carril de emergencia, reacción ante obstáculo sorpresa y slalom corto. Se trabaja la toma de decisiones en menos de 1.5 segundos. Primer uso de radio durante la práctica.",
    ejerciciosCubiertos: ["c-e5638-d7","c-e5638-d8","c-e5638-d9","c-e5638-a1","c-e5638-a2","c-e5638-em1"],
  },
  {
    id: "ses-g1-p5", grupoId: "grp-2026-1", titulo: "Práctica E5638 — Slalom y trayectorias", tipo: "practica",
    fecha: "2026-06-04", hora: "14:00", duracionMin: 240, sede: "Pista 2", instructorId: "inst-001",
    descripcion: "Slalom largo (conos 15 m), óvalo de posiciones (inclinación controlada) y trayectorias ideales en circuito mixto. Se evalúa fluidez de aceleración y suavidad de entrada/salida de curva.",
    ejerciciosCubiertos: ["c-e5638-d10","c-e5638-d11","c-e5638-d12","c-e5638-c1"],
  },
  {
    id: "ses-g1-p6", grupoId: "grp-2026-1", titulo: "Práctica integral — Escolta y formación táctica", tipo: "practica",
    fecha: "2026-06-11", hora: "08:00", duracionMin: 300, sede: "Circuito exterior", instructorId: "inst-001", evaluadorId: "eval-003",
    descripcion: "Sesión de integración: recorrido completo de circuito con todos los ejercicios E5637 y E5638 en secuencia real de servicio. Incluye protocolo de escolta, comunicación radial y registro en bitácora. Se aplica la prechecklist oficial.",
    ejerciciosCubiertos: ["c-e5637-d1","c-e5637-d2","c-e5637-d3","c-e5637-d4","c-e5637-d5","c-e5637-d6","c-e5637-d7","c-e5637-p1","c-e5638-d1","c-e5638-d2","c-e5638-d3","c-e5638-d4","c-e5638-d5","c-e5638-d6","c-e5638-d7","c-e5638-d8","c-e5638-d9","c-e5638-d10","c-e5638-d11","c-e5638-d12","c-e5638-a1","c-e5638-a2","c-e5638-em1"],
  },
  { id: "ses-g1-8", grupoId: "grp-2026-1", titulo: "Cierre y dictamen", tipo: "cierre", fecha: "2026-06-25", hora: "10:00", duracionMin: 180, sede: "Aula A", instructorId: "inst-001", evaluadorId: "eval-003" },
  { id: "ses-g2-1", grupoId: "grp-2026-2", titulo: "Inducción", tipo: "induccion", fecha: "2026-06-15", hora: "09:00", duracionMin: 180, sede: "Aula A", instructorId: "inst-002" },
  { id: "ses-g2-2", grupoId: "grp-2026-2", titulo: "Diagnóstico", tipo: "examen", fecha: "2026-06-17", hora: "09:00", duracionMin: 120, sede: "Aula A", instructorId: "inst-002" },
  {
    id: "ses-g2-p1", grupoId: "grp-2026-2", titulo: "Práctica E5637 — Revisión preoperativa", tipo: "practica",
    fecha: "2026-07-02", hora: "08:00", duracionMin: 180, sede: "Patio de revisión", instructorId: "inst-002",
    descripcion: "Aplicación de la lista de cotejo institucional: inspección sistemática en sentido horario y verificación de equipo de protección personal.",
    ejerciciosCubiertos: ["c-e5637-d1","c-e5637-d2","c-e5637-d3","c-e5637-d4","c-e5637-d5","c-e5637-d6","c-e5637-d7","c-e5637-p1","c-e5637-a1"],
  },
  {
    id: "ses-g2-p2", grupoId: "grp-2026-2", titulo: "Práctica E5638 — Equilibrio y frenado básico", tipo: "practica",
    fecha: "2026-07-09", hora: "14:00", duracionMin: 240, sede: "Pista 1", instructorId: "inst-002",
    descripcion: "Introducción al circuito: equilibrio, dirección a tope y frenado en línea recta. Primera sesión con telemetría activa.",
    ejerciciosCubiertos: ["c-e5638-d1","c-e5638-d2","c-e5638-d3","c-e5638-d4","c-e5638-a1","c-e5638-a2"],
  },
  {
    id: "ses-g2-p3", grupoId: "grp-2026-2", titulo: "Práctica E5638 — Maniobras avanzadas", tipo: "practica",
    fecha: "2026-07-16", hora: "14:00", duracionMin: 240, sede: "Pista 1", instructorId: "inst-002",
    descripcion: "Frenado en curva, efecto giroscópico, cambio de carril de emergencia y reacción sorpresa. Registro de tiempos por sección.",
    ejerciciosCubiertos: ["c-e5638-d5","c-e5638-d6","c-e5638-d7","c-e5638-d8","c-e5638-a1","c-e5638-a2","c-e5638-em1"],
  },
  {
    id: "ses-g2-p4", grupoId: "grp-2026-2", titulo: "Práctica E5638 — Slalom y circuito completo", tipo: "practica",
    fecha: "2026-07-23", hora: "14:00", duracionMin: 300, sede: "Circuito exterior", instructorId: "inst-002",
    descripcion: "Slalom corto y largo, óvalo de posiciones, trayectorias ideales y recorrido integrado. Incluye comunicación radial y bitácora de sesión.",
    ejerciciosCubiertos: ["c-e5638-d9","c-e5638-d10","c-e5638-d11","c-e5638-d12","c-e5638-c1","c-e5638-a1","c-e5638-a2"],
  },
];

export interface Expediente {
  id: string;
  candidatoId: string;
  estandar: string;
  grupoId: string;
  diagnostico: { aplicado: boolean; puntaje: number | null; fecha: string };
  examenTeorico: { aplicado: boolean; puntaje: number | null; aprobado: boolean; fecha: string };
  evaluacionPractica: { aplicada: boolean; criteriosCompetente: number; criteriosTotales: number; fecha: string };
  evidencias: { entregadas: number; requeridas: number; verificadas: number };
  dictamen: "pendiente" | "competente" | "todavia-no-competente";
  certificado: { emitido: boolean; folio: string | null; fechaEmision: string | null };
  alertasIA: string[];
}

export const expedientes: Expediente[] = [
  {
    id: "exp-001",
    candidatoId: "cand-001",
    estandar: "EC1781",
    grupoId: "grp-2026-1",
    diagnostico: { aplicado: true, puntaje: 86, fecha: "2026-05-14" },
    examenTeorico: { aplicado: true, puntaje: 92, aprobado: true, fecha: "2026-05-20" },
    evaluacionPractica: { aplicada: true, criteriosCompetente: 16, criteriosTotales: 17, fecha: "2026-05-25" },
    evidencias: { entregadas: 6, requeridas: 6, verificadas: 6 },
    dictamen: "pendiente",
    certificado: { emitido: false, folio: null, fechaEmision: null },
    alertasIA: [
      "Telemetría GPS coincide con registro radial — sin anomalías",
      "Tiempo de frenado: 28.4 m a 60 km/h (parámetro aceptable ≤ 32 m)",
      "Confianza IA en pre-dictamen: 96.4%",
    ],
  },
  {
    id: "exp-002",
    candidatoId: "cand-002",
    estandar: "EC1781",
    grupoId: "grp-2026-1",
    diagnostico: { aplicado: true, puntaje: 74, fecha: "2026-05-14" },
    examenTeorico: { aplicado: true, puntaje: 81, aprobado: true, fecha: "2026-05-20" },
    evaluacionPractica: { aplicada: true, criteriosCompetente: 14, criteriosTotales: 17, fecha: "2026-05-26" },
    evidencias: { entregadas: 6, requeridas: 6, verificadas: 5 },
    dictamen: "pendiente",
    certificado: { emitido: false, folio: null, fechaEmision: null },
    alertasIA: [
      "Criterio C10 (frenado de emergencia) sin video desde cámara fija — verificar",
      "Pre-dictamen IA: competente con observaciones (confianza 88.2%)",
    ],
  },
  {
    id: "exp-003",
    candidatoId: "cand-003",
    estandar: "EC1781",
    grupoId: "grp-2026-1",
    diagnostico: { aplicado: true, puntaje: 68, fecha: "2026-05-14" },
    examenTeorico: { aplicado: true, puntaje: 76, aprobado: true, fecha: "2026-05-21" },
    evaluacionPractica: { aplicada: false, criteriosCompetente: 0, criteriosTotales: 17, fecha: "—" },
    evidencias: { entregadas: 0, requeridas: 6, verificadas: 0 },
    dictamen: "pendiente",
    certificado: { emitido: false, folio: null, fechaEmision: null },
    alertasIA: [],
  },
  {
    id: "exp-005",
    candidatoId: "cand-005",
    estandar: "EC1781",
    grupoId: "grp-2026-1",
    diagnostico: { aplicado: true, puntaje: 90, fecha: "2026-04-10" },
    examenTeorico: { aplicado: true, puntaje: 95, aprobado: true, fecha: "2026-04-22" },
    evaluacionPractica: { aplicada: true, criteriosCompetente: 17, criteriosTotales: 17, fecha: "2026-05-02" },
    evidencias: { entregadas: 6, requeridas: 6, verificadas: 6 },
    dictamen: "competente",
    certificado: { emitido: true, folio: "CC-EC1781-2026-0187", fechaEmision: "2026-05-15" },
    alertasIA: ["Expediente cerrado y certificado emitido — sin inconsistencias"],
  },
];

export interface ActivityItem {
  id: string;
  hora: string;
  actor: string;
  accion: string;
  objeto: string;
  tipo: "evaluacion" | "dictamen" | "certificado" | "publicacion" | "inscripcion" | "ia";
}

export const activityFeed: ActivityItem[] = [
  { id: "a1", hora: "Hace 4 min", actor: "Cap. Manuel Treviño", accion: "completó evaluación práctica de", objeto: "Sgto. 1° Jorge Ramírez (EC1781)", tipo: "evaluacion" },
  { id: "a2", hora: "Hace 18 min", actor: "Agente IA", accion: "generó pre-dictamen para", objeto: "expediente EXP-001", tipo: "ia" },
  { id: "a3", hora: "Hace 47 min", actor: "Dr. Ernesto Vargas", accion: "emitió dictamen 'Competente' a", objeto: "Sgto. 1° Patricia Vázquez", tipo: "dictamen" },
  { id: "a4", hora: "Hace 1 h", actor: "Sistema", accion: "emitió certificado", objeto: "CC-EC1781-2026-0187", tipo: "certificado" },
  { id: "a5", hora: "Hace 2 h", actor: "Lic. Adriana Cruz", accion: "inscribió a 4 candidatos en", objeto: "EC1781 Gen. 2026-A", tipo: "inscripcion" },
  { id: "a6", hora: "Hace 3 h", actor: "Agente IA", accion: "detectó posible inconsistencia en", objeto: "expediente EXP-002 (criterio C10)", tipo: "ia" },
  { id: "a7", hora: "Ayer", actor: "Administrador", accion: "publicó la versión 1.2 del estándar", objeto: "EC1781", tipo: "publicacion" },
];

export interface RuleFamily {
  codigo: string;
  nombre: string;
  descripcion: string;
  reglas: number;
  color: string;
}

export const ruleFamilies: RuleFamily[] = [
  { codigo: "RG", nombre: "Reglas generales", descripcion: "Aplican a todo el proceso de dictaminación con independencia del criterio", reglas: 6, color: "#1a2744" },
  { codigo: "RE", nombre: "Reglas por evidencia", descripcion: "Validan completitud, integridad y vigencia de cada evidencia requerida", reglas: 8, color: "#0e7490" },
  { codigo: "RL", nombre: "Reglas legales y normativas", descripcion: "Verifican cumplimiento de la NMX, ley de seguridad pública y CONOCER", reglas: 5, color: "#475569" },
  { codigo: "RT", nombre: "Reglas técnicas", descripcion: "Aplican parámetros técnicos del estándar (tiempos, distancias, mediciones)", reglas: 11, color: "#0891b2" },
  { codigo: "RP", nombre: "Reglas procedimentales", descripcion: "Aseguran el correcto orden y método de aplicación del proceso", reglas: 7, color: "#d97706" },
  { codigo: "RV", nombre: "Reglas de validación cruzada", descripcion: "Cruzan información entre evidencias, telemetría y testimonios", reglas: 9, color: "#7c3aed" },
  { codigo: "RD", nombre: "Reglas de decisión", descripcion: "Convierten el conjunto de resultados en el dictamen final", reglas: 4, color: "#dc2626" },
];

export const auditAlerts = [
  { id: "al-1", severidad: "alta" as const, titulo: "Expediente sin video de cámara fija en C10", expedienteId: "exp-002", fecha: "2026-05-26" },
  { id: "al-2", severidad: "media" as const, titulo: "Tiempo entre dictamen y emisión de certificado por debajo del umbral", expedienteId: "exp-005", fecha: "2026-05-15" },
  { id: "al-3", severidad: "baja" as const, titulo: "Bitácora sin firma del instructor en revisión preoperativa", expedienteId: "exp-003", fecha: "2026-05-14" },
];

export const teoricoQuestions = [
  {
    id: "q1",
    enunciado: "¿Cuál es la distancia mínima de frenado aceptable a 60 km/h en superficie seca con motocicleta de alto desempeño?",
    opciones: ["≤ 24 m", "≤ 28 m", "≤ 32 m", "≤ 40 m"],
    correcta: 2,
  },
  {
    id: "q2",
    enunciado: "Durante una escolta perimetral en formación, ¿cuál es la separación mínima entre motocicletas?",
    opciones: ["1.5 m", "2.0 m", "3.0 m", "5.0 m"],
    correcta: 2,
  },
  {
    id: "q3",
    enunciado: "Si durante el recorrido se detecta una falla en el sistema de frenos, el procedimiento institucional indica:",
    opciones: [
      "Continuar a velocidad reducida hasta el final",
      "Detenerse de forma controlada y reportar por radio",
      "Aumentar uso del freno motor y completar el recorrido",
      "Cambiar de motocicleta sin reportar",
    ],
    correcta: 1,
  },
  {
    id: "q4",
    enunciado: "La presión recomendada para el neumático trasero en operación urbana es:",
    opciones: ["28 PSI", "32 PSI", "36 PSI", "42 PSI"],
    correcta: 2,
  },
  {
    id: "q5",
    enunciado: "El equipo de protección personal mínimo obligatorio incluye:",
    opciones: [
      "Casco y guantes",
      "Casco, guantes y botas",
      "Casco, guantes, botas, protección dorsal y rodilleras",
      "Casco integral únicamente",
    ],
    correcta: 2,
  },
];

export const diagnosticItems = [
  { id: "d1", area: "Experiencia previa", pregunta: "¿Cuántos años lleva conduciendo motocicleta en servicio?" },
  { id: "d2", area: "Capacitación", pregunta: "¿Ha cursado entrenamiento formal de conducción defensiva?" },
  { id: "d3", area: "Salud", pregunta: "¿Cuenta con examen médico vigente apto para servicio operativo?" },
  { id: "d4", area: "Habilidades", pregunta: "Calificación auto-reportada en frenado de emergencia (1 a 5)" },
  { id: "d5", area: "Habilidades", pregunta: "Calificación auto-reportada en conducción a baja velocidad (1 a 5)" },
  { id: "d6", area: "Habilidades", pregunta: "Calificación auto-reportada en maniobras de escolta (1 a 5)" },
];

export const certificateTemplate = {
  emisor: "CONOCER — Consejo Nacional de Normalización y Certificación de Competencias Laborales",
  entidad: "CertiCore — Entidad de Certificación y Evaluación de Competencias",
  estandar: "EC1781 — Conducción de motocicleta de alto desempeño en operativos de seguridad",
  vigencia: "5 años",
  folioPrefijo: "CC-EC1781-2026-",
};
